import { atom } from 'jotai';
import { loadable } from 'jotai/utils';
import request from 'lib/request';
import { toDateParam } from 'utils/dates';
import type { AgentCostsResponse, CostComparison } from './types';

export interface DateRange {
  start: string | null; // 'YYYY-MM-DD'
  end: string | null;
}

const isoDaysAgo = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateParam(d);
};
const isoToday = (): string => toDateParam(new Date());

// Global date range scoping the whole summary page. Defaults to the last 30 days
// (also keeps the prod-scale aggregations fast).
export const dateRangeAtom = atom<DateRange>({
  start: isoDaysAgo(30),
  end: isoToday(),
});

// `request.get` returns `{ error: 'AbortError' }` when its AbortController fires;
// the fetch wrappers re-throw it as `new Error('AbortError')`. Callers use this
// to swallow aborts instead of surfacing them as a load failure.
export const isAbortError = (e: unknown): boolean =>
  e instanceof Error && e.message === 'AbortError';

const rangeQuery = (range: DateRange): string => {
  const params = new URLSearchParams();
  if (range.start) params.set('start', range.start);
  if (range.end) params.set('end', range.end);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const agentCostsBaseAtom = atom(async (get) => {
  const range = get(dateRangeAtom);

  const response = await request.get<AgentCostsResponse>(
    `/admin/agent_costs/agents${rangeQuery(range)}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
});

const agentCostsLoadableAtom = loadable(agentCostsBaseAtom);

export const agentCostsAtom = atom((get) => get(agentCostsLoadableAtom));

// Lazy, separate fetch — it hits the Anthropic API (slower), so we don't block
// the dashboard on it. Scoped to the same global date range.
export const fetchCostComparison = async (
  range: DateRange,
  abortController?: AbortController
): Promise<CostComparison> => {
  const response = await request.get<CostComparison>(
    `/admin/agent_costs/cost_comparison${rangeQuery(range)}`,
    abortController
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
};
